import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

export function SignupPage() {
  const [organizationName, setOrganizationName] = useState('');
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
  };

  const handleNameChange = (name: string) => {
    setOrganizationName(name);
    if (!organizationSlug || organizationSlug === generateSlug(organizationName)) {
      setOrganizationSlug(generateSlug(name));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (adminPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (adminPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post<{
        message: string;
        data: {
          organization_id: string;
          organization_slug: string;
          status: string;
          access_token: string;
          refresh_token: string;
          user: { id: string; email: string; role: string; org_id: string; org_slug: string };
        };
      }>('/auth/signup', {
        organization_name: organizationName,
        organization_slug: organizationSlug,
        admin_name: adminName,
        admin_email: adminEmail,
        admin_password: adminPassword,
      });

      // Auto-login after signup
      login(
        response.data.access_token,
        response.data.refresh_token,
        response.data.user.org_id
      );
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary-600 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">SM</span>
          </div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
            Create your organization
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Start your free trial of SearchMyContent
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-lg bg-red-50 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="border-b border-gray-200 pb-4">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Organization Details</h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="org-name" className="label">Organization Name</label>
                  <input
                    id="org-name"
                    name="org-name"
                    type="text"
                    required
                    value={organizationName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="input"
                    placeholder="My Church or Organization"
                  />
                </div>

                <div>
                  <label htmlFor="org-slug" className="label">Organization ID</label>
                  <input
                    id="org-slug"
                    name="org-slug"
                    type="text"
                    required
                    value={organizationSlug}
                    onChange={(e) => setOrganizationSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    className="input"
                    placeholder="my-organization"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Used to identify your organization. Only lowercase letters, numbers, and hyphens.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-4">Admin Account</h3>

              <div className="space-y-4">
                <div>
                  <label htmlFor="admin-name" className="label">Your Name</label>
                  <input
                    id="admin-name"
                    name="admin-name"
                    type="text"
                    value={adminName}
                    onChange={(e) => setAdminName(e.target.value)}
                    className="input"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="admin-email" className="label">Email Address</label>
                  <input
                    id="admin-email"
                    name="admin-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    className="input"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="admin-password" className="label">Password</label>
                  <input
                    id="admin-password"
                    name="admin-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="input"
                    placeholder="At least 8 characters"
                  />
                </div>

                <div>
                  <label htmlFor="confirm-password" className="label">Confirm Password</label>
                  <input
                    id="confirm-password"
                    name="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input"
                    placeholder="Confirm your password"
                  />
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" />
                Creating organization...
              </>
            ) : (
              'Create Organization'
            )}
          </button>

          <p className="text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
